import React, { useState, useEffect, useCallback } from "react";
import { Card, CardHeader, CardBody, Badge, Spinner, Alert } from "reactstrap";
import userServices from "services/userServices";
import Pagination from "views/pages/components/Pagination";
import helpers from "utils/helpers";
import { useTranslation } from "context/TranslationProvider";

const UserFlaggedHistoryTable = ({ userId }) => {
  const { t } = useTranslation();
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0); // 0-indexed for Pagination component
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const fetchFlaggedHistory = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await userServices.getUserFlaggedHistory(userId, {
        page: currentPage + 1,
        limit: rowsPerPage,
      });

      const responseData = res?.data || res;
      if (res?.success || Array.isArray(responseData)) {
        const flagList = Array.isArray(responseData) ? responseData : (responseData.data || []);
        const paginationMeta = responseData.pagination || {};
        setFlags(flagList);
        setTotalRows(paginationMeta.total || flagList.length);
      } else {
        setError(t("page.flagged.toast.fetchError", "Failed to fetch flagged history."));
      }
    } catch (err) {
      console.error("fetchFlaggedHistory error:", err);
      setError(t("page.flagged.toast.fetchError", "Failed to fetch flagged history."));
    } finally {
      setLoading(false);
    }
  }, [userId, currentPage, rowsPerPage, t]);

  useEffect(() => {
    fetchFlaggedHistory();
  }, [fetchFlaggedHistory]);

  const getPriorityBadgeColor = (priority) => {
    const p = String(priority).toLowerCase();
    if (p === "critical") return { bg: "#FDF2F2", text: "#DE3B30", border: "#FBD5D5" };
    if (p === "high") return { bg: "#FFF4ED", text: "#F97316", border: "#FFEDD5" };
    if (p === "medium") return { bg: "#EFF6FF", text: "#2563EB", border: "#DBEAFE" };
    return { bg: "#F8FAFC", text: "#64748B", border: "#E2E8F0" };
  };

  const getStatusBadgeColor = (status) => {
    const s = String(status).toLowerCase();
    if (["actiontaken", "action_taken", "resolved"].includes(s)) return "#10B981";
    if (["dismissed", "closed"].includes(s)) return "#64748B";
    if (["pending", "waiting_user"].includes(s)) return "#F59E0B";
    return "#3B82F6";
  };

  return (
    <Card className="shadow border-0 mt-4">
      <CardHeader className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
        <h3 className="mb-0 font-weight-bold heading-md text-slate-800">
          <i className="fa fa-flag mr-2 text-primary" />
          {t("page.user-profile.header.flaggedHistoryTitle", "Flagged History")}
        </h3>
        <Badge style={{ backgroundColor: "#EFF6FF", color: "#2563EB", fontSize: "11px", fontWeight: 700 }}>
          {totalRows}
        </Badge>
      </CardHeader>
      <CardBody className="p-0">
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" size="sm" />
            <span className="ml-2 text-muted text-sm">{t("common.loading", "Loading...")}</span>
          </div>
        ) : error ? (
          <Alert color="danger" className="m-3 p-3 text-sm">
            {error}
          </Alert>
        ) : flags.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="fa fa-shield-alt fa-2x mb-2 text-slate-300 d-block" />
            <span className="text-sm font-weight-500">
              {t("page.user-profile.header.noFlaggedHistory", "No moderation flags found for this user.")}
            </span>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table align-items-center table-flush mb-0">
                <thead className="thead-light">
                  <tr>
                    <th className="font-weight-700 text-slate-600 text-xs">{t("table.headers.type", "TYPE")}</th>
                    <th className="font-weight-700 text-slate-600 text-xs">{t("table.headers.priority", "PRIORITY")}</th>
                    <th className="font-weight-700 text-slate-600 text-xs">{t("table.headers.status", "STATUS")}</th>
                    <th className="font-weight-700 text-slate-600 text-xs">{t("table.headers.actionTaken", "ACTION TAKEN")}</th>
                    <th className="font-weight-700 text-slate-600 text-xs">{t("page.user-profile.header.createdBy", "CREATED BY")}</th>
                    <th className="font-weight-700 text-slate-600 text-xs">{t("table.headers.createdDate", "DATE")}</th>
                  </tr>
                </thead>
                <tbody>
                  {flags.map((item) => {
                    const priVal = String(item.priority || item.severity || "medium");
                    const stVal = String(item.status || "pending");
                    const priColors = getPriorityBadgeColor(priVal);
                    const stColor = getStatusBadgeColor(stVal);
                    return (
                      <tr key={item._id}>
                        <td>
                          <span className="font-weight-bold text-slate-800 text-sm d-block">
                            {item.flagType ? item.flagType.charAt(0).toUpperCase() + item.flagType.slice(1) : "—"}
                          </span>
                          {item.contentCategory && (
                            <small className="text-muted text-xs d-block">
                              {t(`page.flagged.categories.${item.contentCategory}`, item.contentCategory)}
                            </small>
                          )}
                        </td>
                        <td>
                          <Badge
                            style={{
                              backgroundColor: priColors.bg,
                              color: priColors.text,
                              border: `1px solid ${priColors.border}`,
                              fontSize: "10px",
                              fontWeight: 700,
                            }}
                            className="text-uppercase"
                          >
                            {String(t(`categories.moderation.severity.${priVal}`, priVal) || priVal).toUpperCase()}
                          </Badge>
                        </td>
                        <td>
                          <Badge
                            style={{
                              backgroundColor: `${stColor}15`,
                              color: stColor,
                              border: `1px solid ${stColor}40`,
                              fontSize: "10px",
                              fontWeight: 700,
                            }}
                            className="text-uppercase"
                          >
                            {String(t(`page.flagged.status.${stVal}`, stVal) || stVal)}
                          </Badge>
                        </td>
                        <td>
                          <span className="text-xs font-weight-600 text-slate-700 text-uppercase">
                            {item.actionTaken && item.actionTaken !== "none"
                              ? (item.actionTaken)
                              : "—"}
                          </span>
                        </td>
                        <td>
                          <span className="text-sm font-weight-500 text-slate-700">
                            {item.createdBy || "System"}
                          </span>
                        </td>
                        <td>
                          <span className="text-sm font-weight-500 text-slate-700">
                            {helpers.getFormattedDate(item.createdAt, t)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="pagination-override-margin mb-3">
              <style
                dangerouslySetInnerHTML={{
                  __html: `
                  .pagination-override-margin .pagination-container {
                    margin-left: 0px !important;
                    margin-right: 0px !important;
                    padding-left: 24px !important;
                    padding-right: 24px !important;
                  }
                `,
                }}
              />
              <Pagination
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalRows={totalRows}
                onPageChange={(page) => setCurrentPage(page)}
                onRowsPerPageChange={(size) => {
                  setRowsPerPage(size);
                  setCurrentPage(0);
                }}
                rowsPerPageOptions={[10, 15, 20]}
                t={t}
              />
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default UserFlaggedHistoryTable;
